import classNames from 'classnames';
import { FC, useCallback, useState } from 'react';

import { Button, LazyImage, Heading } from '../../shared/ui';

const makeUrl = (url: string) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}/${url}`;
};

const MAX_SELECTED_CARDS = 4;

type MacListCard = {
  id: number;
  img: string;
  selected: boolean;
  show: boolean;
  deck: string;
  deckCover: string;
  deckColor?: string;
};

interface IMobileCardTileProps {
  item: MacListCard;
  deckCover: string;
  deckColor: string;
  isActive: boolean;
  dimmed: boolean;
  onToggle: () => void;
}

/** Мобильный 1:1: карта в сетке. Тап — выбрать, лупа на карте с видимой лицевой стороной — рассмотреть (как раньше). */
const MobileCardTile: FC<IMobileCardTileProps> = ({ item, deckCover, deckColor, isActive, dimmed, onToggle }) => {
  const faceVisible = item.show || isActive;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      aria-label={isActive ? 'Карта выбрана' : 'Выбрать карту'}
      className={classNames(
        'relative aspect-[110/140] w-full cursor-pointer select-none overflow-hidden rounded-card border-2 transition-all duration-200',
        isActive ? 'scale-[1.02] border-accent shadow-overlay' : 'border-ink-muted',
        dimmed && 'opacity-50',
      )}
      style={!isActive && !faceVisible ? { borderColor: deckColor } : undefined}
      onClick={(e) => {
        // Клик по лупе открывает превью и не должен выбирать карту
        if ((e.target as HTMLElement).closest('button')) return;
        onToggle();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <LazyImage
        src={makeUrl(faceVisible ? item.img : deckCover)}
        allowZoom={faceVisible}
        skeleton
        className="h-full w-full object-cover"
        alt={faceVisible ? String(item.id) : 'deck cover'}
      />
      {isActive && (
        <span className="absolute left-2 top-2 z-[2] flex h-6 w-6 items-center justify-center rounded-full bg-accent text-ink shadow-control">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12.5l4.5 4.5L19 7.5"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </div>
  );
};

export const MacCardList: FC<{
  cards: {
    id: number;
    img: string;
    selected: boolean;
    show: boolean;
    deck: string;
    deckCover: string;
    deckColor?: string;
  }[];
  selectedCards: {
    id: number;
    img: string;
    selected: boolean;
  }[];
  setSelectedCards: (
    a: {
      id: number;
      img: string;
      selected: boolean;
    }[],
  ) => void;
  /** Идентификатор участника — отправляется с выбором, чтобы у каждого клиента был свой выбор */
  participantIdentity?: string;
  /** Отправить выбор по data channel */
  onSubmitViaDataChannel?: (result: Array<{ id: number; img: string; selected: boolean }>) => void;
  /** Групповая терапия: показывать сетку карт в 6 столбцов */
  isGroupTherapy?: boolean;
  /** Мобильный 1:1: компактная шапка со счётчиком, карты без лупы, кнопка «Выбрать» внизу */
  mobile?: boolean;
}> = ({
  cards,
  setSelectedCards,
  selectedCards,
  participantIdentity,
  onSubmitViaDataChannel,
  isGroupTherapy,
  mobile = false,
}) => {
  const [activeItems, setActiveItems] = useState<number[]>([]);

  const handleChooseCard = useCallback(
    (id: number) => {
      if (selectedCards.filter((el) => el.selected).length > 0) return;
      if (activeItems.length === 4 && !activeItems.includes(id)) return;
      if (activeItems.includes(id)) {
        setActiveItems((prev) => prev.filter((el) => el !== id));

        return;
      }
      setActiveItems([...activeItems, id]);
    },
    [activeItems, selectedCards],
  );

  const getChooseToPsy = useCallback(() => {
    const arr = cards.map((el) => {
      if (activeItems.includes(el.id)) {
        return { ...el, selected: !el.selected };
      } else {
        return el;
      }
    });
    const selected = arr.filter((el) => el.selected);

    onSubmitViaDataChannel?.(selected);
    setSelectedCards(selected);
  }, [activeItems, cards, setSelectedCards, onSubmitViaDataChannel]);

  if (mobile) {
    const selectedCount = activeItems.length;
    const limitReached = selectedCount >= MAX_SELECTED_CARDS;

    return (
      <div className="flex h-full min-h-0 w-full flex-col bg-ink-muted px-4 pt-1">
        <div className="flex shrink-0 items-center justify-between gap-3 pb-2">
          <p className="text-ui leading-snug text-white/80">Выберите до {MAX_SELECTED_CARDS} карт</p>
          <span
            className="shrink-0 rounded-full bg-black/30 px-3 py-1 text-ui font-medium text-white"
            aria-live="polite"
          >
            {selectedCount}/{MAX_SELECTED_CARDS}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pb-3 white-scroll">
          <div className="grid grid-cols-2 gap-3 pr-1">
            {cards.map((item) => {
              const deckGroup = cards.find((el) => el.deck === item.deck);
              const isActive = activeItems.includes(item.id);

              return (
                <MobileCardTile
                  key={item.id}
                  item={item}
                  deckCover={deckGroup?.deckCover || ''}
                  deckColor={deckGroup?.deckColor || '#6A6B70'}
                  isActive={isActive}
                  dimmed={limitReached && !isActive}
                  onToggle={() => handleChooseCard(item.id)}
                />
              );
            })}
          </div>
        </div>

        <div className="shrink-0 border-t border-white/10 pb-3 pt-3">
          <Button size="lg" fullWidth onClick={() => getChooseToPsy()} disabled={selectedCount === 0}>
            {selectedCount > 0 ? `Выбрать (${selectedCount})` : 'Выберите карты'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'flex flex-col h-full min-h-0 w-full rounded-chip md:rounded-card overflow-hidden px-4 py-4',
        isGroupTherapy ? 'bg-white relative pb-0' : 'bg-ink-muted',
      )}
    >
      <Heading size="lg" tone={isGroupTherapy ? 'default' : 'inverse'} className="mb-1 shrink-0">
        МАК-карты
      </Heading>
      <p
        className={classNames(
          'mb-3 shrink-0',
          isGroupTherapy ? 'text-body-xl text-ink-muted' : 'text-ui lg:text-body-xl text-white',
        )}
      >
        Выберите максимум 4 карты из колоды
      </p>

      {/* В групповой терапии кнопка поверх (absolute), карты с нормальными пропорциями 110:140 и скроллом */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 white-scroll">
        <div
          className={classNames(
            'grid gap-3 auto-rows-fr w-full min-w-0',
            isGroupTherapy ? 'grid-cols-2 lg:grid-cols-6' : 'grid-cols-2 lg:grid-cols-3',
          )}
        >
          {cards.map((item) => {
            if (item.show || activeItems.includes(item.id)) {
              return (
                <div
                  className={classNames(
                    'rounded-control overflow-hidden border-2 aspect-[110/140] cursor-pointer',
                    activeItems.includes(item.id) ? 'border-accent' : 'border-ink-muted',
                  )}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('button')) return;
                    handleChooseCard(item.id);
                  }}
                  key={item.id}
                >
                  <div className="w-full h-full">
                    <LazyImage
                      src={makeUrl(item.img)}
                      allowZoom={true}
                      className="w-full h-full object-cover"
                      alt={String(item.id)}
                    />
                  </div>
                </div>
              );
            }
            const deckGroup = cards.find((el) => el.deck === item.deck);
            const deckCover = deckGroup?.deckCover || '';
            const borderColor = deckGroup?.deckColor || '#6A6B70';

            return (
              <div
                className="rounded-control overflow-hidden border-2 aspect-[110/140] cursor-pointer"
                style={{ borderColor }}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('button')) return;
                  handleChooseCard(item.id);
                }}
                key={item.id}
              >
                <div className="w-full h-full">
                  <LazyImage
                    src={makeUrl(deckCover)}
                    allowZoom={false}
                    className="w-full h-full object-cover"
                    alt="deck cover"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className={classNames(
          'flex justify-center shrink-0',
          isGroupTherapy ? 'absolute bottom-4 left-1/2 -translate-x-1/2 z-10' : 'pt-3',
        )}
      >
        <Button
          size="md"
          fullWidth={!isGroupTherapy}
          className={classNames(isGroupTherapy && 'w-[200px]')}
          onClick={() => getChooseToPsy()}
          disabled={activeItems.length === 0}
        >
          Выбрать
        </Button>
      </div>
    </div>
  );
};
